import React, {
    useLayoutEffect,
    useEffect,
    useState,
    useCallback,
} from 'react';
import './debate-game.css';
import {
    goBack,
    goToScreen,
    goToScreenAndReset,
    setHistoryScreenState,
} from '../../actions/router';
import { connect } from 'react-redux';
import {
    DEBATE_GAME_SCREEN,
    MAIN_GAME_SCREEN,
    SETTINGS_SCREEN,
    SPEAKER_GAME,
} from '../../constants/routes';
import Copy from '../../copy/debate';
import classNames from 'classnames';
import { Panel } from '../../components/panel';
import { IconButton } from '../../components/icon-button';
import { TutorialModalParent } from '../../components/modal-parents/tutorial-modal';
import { ProgressUpdateModalParent } from '../../components/modal-parents/progress-update-modal';
import { completeTutorial } from '../../actions/tutorials';
import { progressTime, updateOverallScores } from '../../actions/active-game';
import { Heading } from '../../components/heading';
import { TopicDecision } from '../../components/topic-decision';
import { SpeechBuilder } from './speech-builder';
import { SpeechTracker } from './speech-builder/speech-tracker';
import { returnDebateScores } from '../../helpers/active-game';
import { SpeakerGameScreen } from './speaker-game-screen';
import { GameSettingsModalParent } from '../../components/modal-parents/game-settings-modal';
import { AudioManager } from '../../controllers/audioController';
const gameSteps = ['topic-decision', 'build-speech', 'speaker'];
const initialNullObject = {
    0: null,
    1: null,
    2: null,
    3: null,
    4: null,
};

const DebateGameScreen = props => {
    const {
        goBack,
        goToSettings,
        continueTo,
        continueRoute,
        historyScreenState,
        tutorialCompleted,
        completeDebateTutorial,
        speakerTutorialCompleted,
        completeSpeakerTutorial,
        previousScores,
        submitOverallScores,
        timedGames,
        difficulty,
        party,
        audioAvailable,
        progressGame,
    } = props;

    const [activeTopic, setActiveTopic] = useState(null);

    const [userSpeech, setUserSpeech] = useState(initialNullObject);
    const middleOptionsChosen = [userSpeech[1], userSpeech[2], userSpeech[3]];

    const [audioObject, setAudioObject] = useState(initialNullObject);
    const audioArray = Object.values(audioObject).filter(
        audio => audio !== null,
    );

    const [activeAudio, setActiveAudio] = useState(null);
    const [audioPlaying, setAudioPlaying] = useState(false);

    const [activeSpeechStep, setActiveSpeechStep] = useState(0);
    const [gameStep, setGameStep] = useState(0);

    const [currentGameScores, setCurrentGameScores] = useState({});
    const [progressUpdateVisible, setProgressUpdateVisible] = useState(false);
    const [responseFeedback, setResponseFeedback] = useState(null);

    const [settingsOptionChosen, setSettingsOptionChosen] = useState(
        'Requires rapid clicking.',
    );
    const [settingsChosen, setSettingsChosen] = useState(false);
    const [accessibleVersion, setAccessibleVersion] = useState(!timedGames);

    const [speakerGamePerformance, setSpeakerGamePerformance] = useState(null);

    const modalVisible =
        progressUpdateVisible || !tutorialCompleted || !settingsChosen;

    useLayoutEffect(() => {
        if (historyScreenState.screen === DEBATE_GAME_SCREEN) {
            setActiveTopic(historyScreenState.state.activeTopic);
            setUserSpeech(historyScreenState.state.currentSpeech);
            setAudioObject(historyScreenState.state.audioObject);
            setGameStep(historyScreenState.state.gameStep);
            setActiveSpeechStep(historyScreenState.state.activeSpeechStep);
            setSettingsChosen(historyScreenState.state.settingsChosen);
            setSettingsOptionChosen(
                historyScreenState.state.settingsOptionChosen,
            );
        }
    }, [historyScreenState]);

    useEffect(() => {
        if (!timedGames) {
            setSettingsOptionChosen('No clicking.');
            setSettingsChosen(true);
        }
    }, [timedGames]);

    useEffect(() => {
        if (settingsOptionChosen === 'No clicking.') {
            setAccessibleVersion(true);
        } else {
            setAccessibleVersion(false);
        }
    }, [settingsOptionChosen]);

    useEffect(() => {
        if (speakerGamePerformance !== null) {
            if (speakerGamePerformance > 2) {
                setProgressUpdateVisible(true);
            } else {
                setCurrentGameScores(Copy.missed.scores);
                setResponseFeedback(Copy.missed.feedback);
                setProgressUpdateVisible(true);
            }
        }
    }, [speakerGamePerformance]);

    const speechData = Copy.debate[difficulty];

    const possibleSpeeches = speechData.filter(
        speech => speech.party !== party,
    );

    const activeSpeech =
        activeTopic !== null
            ? possibleSpeeches.filter(
                  speech => speech.interest === activeTopic,
              )[0]
            : null;

    const openingOptions = activeSpeech
        ? activeSpeech.clip.filter(option => option.clipType === 'start')
        : null;

    const middleOptions = activeSpeech
        ? activeSpeech.clip.filter(
              option =>
                  !middleOptionsChosen.includes(option) &&
                  option.clipType === 'main',
          )
        : null;

    const closingOptions = activeSpeech
        ? activeSpeech.clip.filter(option => option.clipType === 'end')
        : null;

    const playTrack = useCallback(
        id => {
            AudioManager.play(audioArray[id], {
                onEnd: () => {
                    if (activeAudio + 1 >= audioArray.length) {
                        setActiveAudio(null);
                        setAudioPlaying(false);

                        return;
                    }
                    return setActiveAudio(id + 1);
                },
            });
        },
        [activeAudio, audioArray],
    );

    useEffect(() => {
        if (activeAudio !== null) {
            playTrack(activeAudio);
        }
    }, [activeAudio, playTrack]);

    const loadAudio = id => {
        AudioManager.load({
            audio: [id],
        });
    };

    const toggleAudioPlaying = () => {
        if (activeAudio !== null) {
            try {
                AudioManager.stopAll();
                AudioManager.getData(
                    audioArray[activeAudio],
                ).source.onended = null;
                setActiveAudio(null);
                setAudioPlaying(false);
            } catch (err) {
                setActiveAudio(null);
                setAudioPlaying(false);
            }
            return true;
        }

        if (AudioManager.isMuted() === true) {
            AudioManager.unMute();
        }
        playTrack(0);
        setActiveAudio(0);
        setAudioPlaying(true);
    };

    const submitResponse = () => {
        if (audioPlaying) {
            toggleAudioPlaying();
        }

        const openPoints = userSpeech[0].points;
        const middlePoints =
            userSpeech[1].points + userSpeech[2].points + userSpeech[3].points;
        const closePoints = userSpeech[4].points;

        const scores = returnDebateScores(
            activeSpeech.result,
            openPoints + middlePoints + closePoints,
        );
        setCurrentGameScores(scores.scores);
        setResponseFeedback(scores.feedback);
        setGameStep(2);
    };

    const completeGame = () => {
        if (continueRoute === MAIN_GAME_SCREEN) {
            submitOverallScores(previousScores, currentGameScores);
            progressGame();
        }
        continueTo(continueRoute);
    };

    const clickBack = () => {
        if (gameStep === 0) {
            return goBack();
        }

        if (audioPlaying) {
            toggleAudioPlaying();
        }
        setUserSpeech(initialNullObject);
        setAudioObject(initialNullObject);
        setActiveSpeechStep(0);
        setGameStep(gameStep - 1);
    };

    const debatePageClasses = classNames({
        'debate-game': true,
        'debate-game--speech-builder': gameStep === 1,
        'debate-game--hidden': modalVisible,
    });

    return (
        <>
            {gameStep < 2 ? (
                <div className={debatePageClasses} aria-hidden={modalVisible}>
                    <IconButton
                        iconName="back"
                        size="small"
                        type="essential"
                        withSpacing={true}
                        onClick={() => clickBack()}
                        isDisabled={modalVisible || gameStep === 2}
                    />
                    <Panel
                        flexDirection="column"
                        withPadding={gameStep === 0}
                        withSpacing={false}
                        color="purple"
                        currentPage={`debate game ${gameSteps[gameStep]}`}
                        key={gameStep}
                    >
                        <div>
                            {gameStep !== 2 && (
                                <Heading
                                    headingType="h3"
                                    colorScheme="dark"
                                    withSpacing={true}
                                    size="medium"
                                >
                                    {Copy.heading[gameSteps[gameStep]]}
                                </Heading>
                            )}
                        </div>
                        {gameStep === 0 ? (
                            <TopicDecision
                                options={possibleSpeeches}
                                selectTopic={topic => {
                                    setActiveTopic(topic);
                                    setGameStep(1);
                                }}
                                modalVisible={modalVisible}
                            />
                        ) : (
                            gameStep === 1 &&
                            activeSpeech !== null && (
                                <>
                                    <SpeechBuilder
                                        userSpeech={userSpeech}
                                        setUserSpeech={setUserSpeech}
                                        setAudioObject={setAudioObject}
                                        activeSpeechStep={activeSpeechStep}
                                        activeSpeechOptions={
                                            activeSpeechStep === 0
                                                ? openingOptions
                                                : activeSpeechStep === 4
                                                ? closingOptions
                                                : activeSpeechStep === 5
                                                ? null
                                                : middleOptions
                                        }
                                        setActiveSpeechStep={
                                            setActiveSpeechStep
                                        }
                                        loadAudio={loadAudio}
                                    />
                                </>
                            )
                        )}
                    </Panel>
                    {gameStep === 1 && (
                        <SpeechTracker
                            userSpeech={userSpeech}
                            setUserSpeech={setUserSpeech}
                            activeSpeechStep={activeSpeechStep}
                            setActiveSpeechStep={setActiveSpeechStep}
                            audioPlaying={audioPlaying}
                            submitResponse={() => submitResponse()}
                            audioArrayNull={audioArray.every(
                                audio => audio === null,
                            )}
                            toggleAudioPlaying={() => toggleAudioPlaying()}
                            audioAvailable={audioAvailable}
                        />
                    )}
                    <IconButton
                        iconName="settings"
                        size="small"
                        type="essential"
                        withSpacing={true}
                        onClick={() => {
                            if (audioPlaying) {
                                toggleAudioPlaying();
                            }
                            goToSettings(
                                activeTopic,
                                userSpeech,
                                audioObject,
                                gameStep,
                                activeSpeechStep,
                                settingsChosen,
                                settingsOptionChosen,
                            );
                        }}
                        isDisabled={modalVisible || gameStep === 2}
                    />
                </div>
            ) : (
                <SpeakerGameScreen
                    goBack={() => setGameStep(1)}
                    goToSettings={(timeLeft, performance) =>
                        goToSettings(
                            activeTopic,
                            userSpeech,
                            audioObject,
                            gameStep,
                            activeSpeechStep,
                            settingsChosen,
                            settingsOptionChosen,
                            currentGameScores,
                            responseFeedback,
                            timeLeft,
                            performance,
                        )
                    }
                    tutorialCompleted={
                        speakerTutorialCompleted || accessibleVersion
                    }
                    historyScreenState={historyScreenState}
                    completeSpeakerTutorial={completeSpeakerTutorial}
                    accessibleVersion={accessibleVersion}
                    completeSpeakerGame={performance => {
                        setSpeakerGamePerformance(performance);
                    }}
                    setCurrentGameScores={setCurrentGameScores}
                    setResponseFeedback={setResponseFeedback}
                />
            )}
            {!tutorialCompleted && (
                <TutorialModalParent
                    screen="debate"
                    onClick={() => completeDebateTutorial()}
                />
            )}
            {tutorialCompleted && !settingsChosen && (
                <GameSettingsModalParent
                    screen="debate"
                    checked={settingsOptionChosen}
                    onClick={() => setSettingsChosen(true)}
                    onRadioSelect={e => setSettingsOptionChosen(e.target.value)}
                />
            )}
            {progressUpdateVisible === true && (
                <ProgressUpdateModalParent
                    previousScores={previousScores}
                    gameScores={currentGameScores}
                    feedback={responseFeedback}
                    onClick={() => completeGame()}
                />
            )}
        </>
    );
};

const mapStateToProps = state => {
    return {
        history: state.router.history,
        continueRoute: state.router.history[state.router.history.length - 2],
        historyScreenState: state.router.historyScreenState,
        tutorialCompleted:
            state.activeGame.tutorialsCompleted[DEBATE_GAME_SCREEN],
        speakerTutorialCompleted:
            state.activeGame.tutorialsCompleted[SPEAKER_GAME],
        previousScores: state.activeGame.overallScores,
        timedGames: state.settings.accessibility.timedGames,
        difficulty: state.settings.gameSettings.difficulty,
        party: state.settings.gameSettings.party,
        audioAvailable: state.settings.audio,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        goBack: () => {
            dispatch(goBack());
        },
        goToSettings: (
            activeTopic,
            currentSpeech,
            audioObject,
            gameStep,
            activeSpeechStep,
            settingsChosen,
            settingsOptionChosen,
            gameScores,
            responseFeedback,
            timeLeft,
            performance,
        ) => {
            dispatch(
                setHistoryScreenState(DEBATE_GAME_SCREEN, {
                    activeTopic,
                    currentSpeech,
                    audioObject,
                    gameStep,
                    activeSpeechStep,
                    settingsChosen,
                    settingsOptionChosen,
                    gameScores,
                    responseFeedback,
                    timeLeft,
                    performance,
                }),
            );
            dispatch(goToScreen(SETTINGS_SCREEN));
        },
        continueTo: route => {
            dispatch(goToScreenAndReset(route));
        },
        completeDebateTutorial: () => {
            dispatch(completeTutorial(DEBATE_GAME_SCREEN));
        },
        completeSpeakerTutorial: () => {
            dispatch(completeTutorial(SPEAKER_GAME));
        },
        submitOverallScores: (previousScores, gameScores) => {
            dispatch(updateOverallScores(previousScores, gameScores));
        },
        progressGame: () => {
            dispatch(progressTime('debate'));
        },
    };
};

const connected = connect(
    mapStateToProps,
    mapDispatchToProps,
)(DebateGameScreen);
export { connected as DebateGameScreen };
